/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { action, makeObservable, observable } from "mobx"
import {
  BookingAnswers,
  BookingConsentInput,
  BookingPatientInput,
  BookingRequest,
  BookingResult,
  BookingSlot,
} from "@medaica/common/types"
import { resolveUsTimeZone } from "../timezones"

type ForWhom = "me" | "someoneElse"
type WizardConsents = { telehealth: boolean; privacyNotice: boolean; appointmentEmails: boolean }

// QA label control owns this (O5). v1 is the FIRST production consent text: the Medaica
// Consent Form from legal (bookingCopy.consent.document) plus the privacy/emails texts.
// The pre-production placeholder wording that shipped only to dev was never a released
// version and is not counted. Bump on ANY consent-copy change from here on, so stored
// rows stay attributable to the text the patient was shown.
const CONSENT_POLICY_VERSION = 1

const emptyConsents = (): WizardConsents => ({ telehealth: false, privacyNotice: false, appointmentEmails: false })

// Auto-detect the patient's zone, clamped to a supported US zone (the picker is US-only).
const guessTimeZone = (): string => {
  try {
    return resolveUsTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
  } catch {
    return resolveUsTimeZone("")
  }
}

/**
 * Holds the patient's wizard input (05 §4). Deliberately separate from the
 * session store so it survives a mid-wizard session expiry (05 §1 resume). The
 * screens read/write it; `toBookingRequest` maps it to the 03 §3 payload.
 */
class WizardStore {
  forWhom: ForWhom = "me"
  patient: BookingPatientInput | null = null
  consents: WizardConsents = emptyConsents()
  answers: BookingAnswers | null = null
  selectedSlot: BookingSlot | null = null
  bookingResult: BookingResult | null = null
  timeZone: string = guessTimeZone()

  constructor() {
    makeObservable(this, {
      forWhom: observable,
      patient: observable,
      consents: observable,
      answers: observable,
      selectedSlot: observable,
      bookingResult: observable,
      timeZone: observable,
      setForWhom: action,
      setPatient: action,
      setConsent: action,
      setAnswers: action,
      setSelectedSlot: action,
      setTimeZone: action,
      setBookingResult: action,
      reset: action,
    })
  }

  get allConsentsGranted(): boolean {
    return this.consents.telehealth && this.consents.privacyNotice && this.consents.appointmentEmails
  }

  setForWhom(forWhom: ForWhom): void {
    this.forWhom = forWhom
  }

  setPatient(patient: BookingPatientInput): void {
    this.patient = patient
  }

  setConsent(type: keyof WizardConsents, value: boolean): void {
    this.consents = { ...this.consents, [type]: value }
  }

  setAnswers(answers: BookingAnswers): void {
    this.answers = answers
  }

  setSelectedSlot(slot: BookingSlot | null): void {
    this.selectedSlot = slot
  }

  setTimeZone(timeZone: string): void {
    this.timeZone = timeZone
  }

  setBookingResult(result: BookingResult): void {
    this.bookingResult = result
  }

  reset(): void {
    this.forWhom = "me"
    this.patient = null
    this.consents = emptyConsents()
    this.answers = null
    this.selectedSlot = null
    this.bookingResult = null
    // timeZone is intentionally preserved across "Book another".
  }

  toBookingRequest(requestId: string): BookingRequest {
    if (!this.patient || !this.selectedSlot) {
      throw new Error("Cannot build a booking request before the patient and slot are set")
    }
    const consents: BookingConsentInput[] = [
      { consentType: "telehealth", granted: this.consents.telehealth, policyVersion: CONSENT_POLICY_VERSION },
      { consentType: "privacy_notice", granted: this.consents.privacyNotice, policyVersion: CONSENT_POLICY_VERSION },
      {
        consentType: "appointment_emails",
        granted: this.consents.appointmentEmails,
        policyVersion: CONSENT_POLICY_VERSION,
      },
    ]
    return {
      requestId,
      patient: this.patient,
      consents,
      answers: this.answers ?? undefined,
      slotStart: this.selectedSlot.startUtc,
      timeZone: this.timeZone,
      bookedForMe: this.forWhom === "me",
    }
  }
}

export default WizardStore
export type { ForWhom, WizardConsents }
